import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const bypassForms = new WeakSet();
const processingForms = new WeakSet();

function getSelectedAddons(form) {
  return Array.from(form.querySelectorAll('[data-flf-addon-select]'))
    .filter((select) => select instanceof HTMLSelectElement && select.value)
    .map((select) => {
      const wrapper = select.closest('[data-flf-addon]');
      const properties = {};

      wrapper?.querySelectorAll('[data-flf-addon-property]').forEach((field) => {
        if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) return;
        const value = field.value.trim();
        const name = field.dataset.flfAddonProperty;
        if (name && value) properties[name] = value;
      });

      return {
        id: Number(select.value),
        quantity: Number(select.dataset.flfAddonQuantity || 1),
        properties,
      };
    })
    .filter((item) => Number.isFinite(item.id) && item.id > 0);
}

function getMainItem(form) {
  const formData = new FormData(form);
  const id = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity')) || 1;
  const properties = {};

  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (!match) continue;
    const text = String(value).trim();
    if (text) properties[match[1]] = text;
  }

  const item = { id, quantity, properties };

  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);

  return item;
}

function updateConditionalFields(select) {
  const details = select.closest('[data-flf-addon]')?.querySelector('[data-flf-addon-details]');
  if (details instanceof HTMLElement) details.hidden = !select.value;
}

function updateAcceleratedCheckout(form) {
  form.querySelector('.product-form-buttons')?.classList.toggle(
    'flf-addons-selected',
    getSelectedAddons(form).length > 0
  );
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
    .filter(Boolean);
}

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems && typeof cartItems.fetchCartData === 'function') {
    return cartItems.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
  return response.json();
}

function disableAddButton(productForm, disabled) {
  productForm.querySelectorAll('add-to-cart-component').forEach((component) => {
    if (disabled && typeof component.disable === 'function') component.disable();
    if (!disabled && typeof component.enable === 'function') component.enable();
  });
}

async function submitBatch(form, productForm, mainItem, addons) {
  const items = [mainItem, ...addons];
  const deferred = CartLinesUpdateEvent.createPromise();

  productForm.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: items.map((item) => ({
      merchandiseId: String(item.id),
      quantity: item.quantity,
    })),
    promise: deferred.promise,
  }));

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items,
        sections: getCartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.status || data.errors) {
      const message = data.description || data.message || data.errors || 'Add to cart failed';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }

    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'flf-addons',
        sourceId: productForm.id?.toString?.() || '',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        productId: productForm.dataset.productId,
        sections: data.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

document.addEventListener('submit', async (event) => {
  const form = event.target;

  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches('form[data-type="add-to-cart-form"]')) return;
  if (!form.querySelector('[data-flf-addons]')) return;

  if (bypassForms.has(form)) {
    bypassForms.delete(form);
    return;
  }

  const addons = getSelectedAddons(form);
  if (!addons.length) return;

  const productForm = form.closest('product-form-component');
  if (!productForm || processingForms.has(form)) return;

  const mainItem = getMainItem(form);
  if (!Number.isFinite(mainItem.id) || mainItem.id <= 0) return;

  // Only take over when a real paid add-on is selected.
  // No-add-on purchases remain 100% Horizon-native.
  event.preventDefault();
  event.stopImmediatePropagation();

  processingForms.add(form);
  disableAddButton(productForm, true);

  try {
    await submitBatch(form, productForm, mainItem, addons);
  } catch (error) {
    console.error('FLF add-on batch error:', error);

    // Safety fallback: if custom batch add fails, let Horizon add the main product normally
    // so a customer is never blocked from purchasing the base product.
    bypassForms.add(form);
    form.requestSubmit();
  } finally {
    processingForms.delete(form);
    disableAddButton(productForm, false);
  }
}, true);

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-flf-addon-select]')) return;

  updateConditionalFields(select);
  const form = select.closest('form[data-type="add-to-cart-form"]');
  if (form) updateAcceleratedCheckout(form);
});

function initialize(root = document) {
  root.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
    if (!(form instanceof HTMLFormElement) || !form.querySelector('[data-flf-addons]')) return;

    form.querySelectorAll('[data-flf-addon-select]').forEach((select) => {
      if (select instanceof HTMLSelectElement) updateConditionalFields(select);
    });

    updateAcceleratedCheckout(form);
  });
}

initialize();
new MutationObserver(() => initialize()).observe(document.documentElement, {
  childList: true,
  subtree: true,
});

// Build Your Party — Step 3 theme selector.
function updatePartyThemeFields(select) {
  const details = select.closest('[data-flf-party-details]');
  const customWrap = details?.querySelector('[data-flf-custom-theme]');
  const customInput = details?.querySelector('[data-flf-custom-theme-input]');
  const isOther = select.value === 'Other';

  if (customWrap instanceof HTMLElement) customWrap.hidden = !isOther;
  if (customInput instanceof HTMLInputElement) {
    customInput.required = isOther;
    if (!isOther) customInput.value = '';
  }
}

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-flf-party-theme]')) return;
  updatePartyThemeFields(select);
});

function initializePartyDetails(root = document) {
  root.querySelectorAll('[data-flf-party-theme]').forEach((select) => {
    if (select instanceof HTMLSelectElement) updatePartyThemeFields(select);
  });
}

initializePartyDetails();
new MutationObserver(() => initializePartyDetails()).observe(document.documentElement, {
  childList: true,
  subtree: true,
});


// Build Your Party — staged Details → Extras → Recipe flow.
function getBuilder(form) {
  return form?.querySelector('[data-flf-builder]');
}

function setBuilderStep(form, step) {
  const builder = getBuilder(form);
  if (!(builder instanceof HTMLElement)) return;

  builder.querySelectorAll('[data-flf-builder-panel]').forEach((panel) => {
    const active = Number(panel.dataset.flfBuilderPanel) === Number(step);
    panel.hidden = !active;
    panel.classList.toggle('is-active', active);
  });

  builder.querySelectorAll('[data-flf-progress]').forEach((item) => {
    const itemStep = Number(item.dataset.flfProgress);
    item.classList.toggle('is-active', itemStep === Number(step));
    item.classList.toggle('is-done', itemStep < Number(step));
  });

  const finalActions = form.querySelector('[data-flf-final-actions]');
  if (finalActions instanceof HTMLElement) finalActions.hidden = Number(step) !== 5;

  if (Number(step) === 5) updatePartyRecipe(form);
  builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function validatePartyDetails(form) {
  const panel = form.querySelector('[data-flf-builder-panel="3"]');
  if (!(panel instanceof HTMLElement)) return true;
  const required = Array.from(panel.querySelectorAll('select[required], input[required], textarea[required]'));
  for (const field of required) {
    if (typeof field.reportValidity === 'function' && !field.reportValidity()) return false;
  }
  return true;
}

function getCurrentVariant(form, builder) {
  const id = Number(new FormData(form).get('id'));
  const json = builder?.querySelector('[data-flf-variants-json]')?.textContent || '[]';
  try {
    const variants = JSON.parse(json);
    return variants.find((variant) => Number(variant.id) === id) || null;
  } catch (error) {
    return null;
  }
}

function formatMoney(cents, builder) {
  if (!Number.isFinite(Number(cents))) return '';
  const amount = Number(cents) / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency', currency: 'CAD'
    }).format(amount);
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
}

function addRecipeRow(list, label, value) {
  if (!value) return;
  const dt = document.createElement('dt');
  const dd = document.createElement('dd');
  dt.textContent = label;
  dd.textContent = value;
  list.append(dt, dd);
}

function selectedAddonSummary(form) {
  return Array.from(form.querySelectorAll('[data-flf-addon-select]'))
    .filter((select) => select instanceof HTMLSelectElement && select.value)
    .map((select) => select.options[select.selectedIndex]?.textContent?.trim())
    .filter(Boolean);
}

function updatePartyRecipe(form) {
  const builder = getBuilder(form);
  if (!(builder instanceof HTMLElement)) return;
  const list = builder.querySelector('[data-flf-recipe-list]');
  const price = builder.querySelector('[data-flf-recipe-price]');
  if (!(list instanceof HTMLElement)) return;

  list.innerHTML = '';
  const variant = getCurrentVariant(form, builder);
  const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
  addRecipeRow(list, 'Colour', variantTitle);

  const fieldValue = (name) => {
    const field = form.querySelector(`[name="properties[${name}]"]`);
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement
      ? field.value.trim() : '';
  };

  addRecipeRow(list, 'Occasion', fieldValue('Occasion'));
  const theme = fieldValue('Theme') === 'Other' ? fieldValue('Custom theme') : fieldValue('Theme');
  addRecipeRow(list, 'Theme', theme);
  addRecipeRow(list, 'Name / personalization', fieldValue('Name / personalization'));
  addRecipeRow(list, 'Age / number', fieldValue('Age / number'));
  addRecipeRow(list, 'Party notes', fieldValue('Party notes'));

  const addons = selectedAddonSummary(form);
  addRecipeRow(list, 'Extras', addons.length ? addons.join(' · ') : 'None');

  if (price instanceof HTMLElement) {
    price.textContent = variant?.price != null ? `Base design: ${formatMoney(variant.price, builder)}` : '';
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-flf-next], [data-flf-back]');
  if (!(button instanceof HTMLButtonElement)) return;
  const form = button.closest('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;

  if (button.dataset.flfNext === '4' && !validatePartyDetails(form)) return;
  const next = button.dataset.flfNext || button.dataset.flfBack;
  if (next) setBuilderStep(form, Number(next));
});

function initializePartyBuilder(root = document) {
  root.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    const builder = getBuilder(form);
    if (!(builder instanceof HTMLElement) || builder.dataset.flfBuilderInitialized === 'true') return;
    builder.dataset.flfBuilderInitialized = 'true';

    const extras = builder.querySelector('[data-flf-addons]');
    const empty = builder.querySelector('[data-flf-empty-extras]');
    if (empty instanceof HTMLElement) empty.hidden = Boolean(extras);

    setBuilderStep(form, 3);
  });
}

initializePartyBuilder();
new MutationObserver(() => initializePartyBuilder()).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
